import { CheckCircle2, ChevronDown, Circle, Loader2, MessageSquare, Wrench, XCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { RunDetail } from "../../api/hooks";
import { formatNumber, formatTime } from "../../lib/format";

type Execution = NonNullable<RunDetail["execution"]>;
type Step = Execution["activity"][number];
type TraceEvent = NonNullable<Execution["trace"]>[number];

function ToolStep({ step, onInspect }: { step: Step; onInspect: () => void }) {
  const { t } = useTranslation();
  const status = step.status === "running" || step.status === "completed" || step.status === "cancelled"
    ? t(`report.traceTool.${step.status}`) : t("report.traceTool.failed", { code: step.status });
  return (
    <details className="group/tool min-w-0 flex-1" onToggle={(event) => { if (event.currentTarget.open) onInspect(); }}>
      <summary className="flex min-h-10 cursor-pointer flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-sm focus-visible:outline-2 focus-visible:outline-ring">
        <span className="flex items-center gap-1.5 break-all font-mono text-xs"><ChevronDown className="h-3 w-3 shrink-0 -rotate-90 group-open/tool:rotate-0" aria-hidden />{step.operation ?? step.tool}</span>
        <span className={`text-xs ${step.status === "running" ? "text-foreground" : step.status === "completed" ? "text-muted-foreground" : "text-negative"}`}>{status}</span>
      </summary>
      <div className="space-y-2 pb-3 text-xs">
        <p className="text-muted-foreground">{t("report.traceInput")}</p>
        <pre className="max-h-44 overflow-auto whitespace-pre-wrap break-all rounded-md bg-muted p-3">{JSON.stringify(step.params, null, 2)}</pre>
        {step.resultPreview !== undefined ? <>
          <p className="text-muted-foreground">{t("report.traceResult")}</p>
          <pre className="max-h-52 overflow-auto whitespace-pre-wrap break-all rounded-md bg-muted p-3">{step.resultPreview}</pre>
          {step.resultTruncated ? <p className="text-muted-foreground">{t("report.traceResultTruncated")}</p> : null}
        </> : null}
        <p className="text-muted-foreground">{t("report.stepCredits", { credits: step.credits, stored: step.fromStore })}</p>
        {step.finishedAt ? <p className="text-muted-foreground">{t("report.traceFinishedAt", { time: formatTime(step.finishedAt) })}</p> : null}
      </div>
    </details>
  );
}

export function AgentTrace({ run }: { run: RunDetail }) {
  const { t } = useTranslation();
  const busy = run.status === "queued" || run.status === "running";
  const execution = run.execution;
  const [disclosure, setDisclosure] = useState<{ id: string; open: boolean } | null>(null);
  const open = disclosure?.id === run.id ? disclosure.open : busy;
  const viewport = useRef<HTMLDivElement>(null);
  const follow = useRef(true);
  // Earlier tool-only runs retain a useful trace without fabricating model events.
  const events: TraceEvent[] = execution?.trace ?? execution?.activity.map((step, index) => ({ id: index, at: step.at, kind: "tool", toolIndex: index })) ?? [];
  const latest = events.at(-1);
  const latestToolState = execution?.activity.at(-1)?.status;
  useEffect(() => { follow.current = true; }, [run.id]);
  useEffect(() => {
    if (busy && open && follow.current && viewport.current) viewport.current.scrollTop = viewport.current.scrollHeight;
  }, [busy, open, run.id, latest?.id, latestToolState]);
  if (!busy && !execution) return null;
  const latestStep = latest?.kind === "tool" ? execution?.activity[latest.toolIndex ?? -1] : undefined;
  const current = !busy ? t(`run.status.${run.status}`)
    : run.status === "queued" ? t("report.traceQueued")
      : latestStep?.status === "running" ? t("report.traceUsing", { tool: latestStep.operation ?? latestStep.tool })
        : latest?.kind === "thinking" || latest?.kind === "reporting" || latest?.kind === "starting" ? t(`report.traceEvent.${latest.kind}`)
          : t("report.traceWorking");
  return (
    <details open={open} onToggle={(event) => {
      if (event.currentTarget.open !== open) setDisclosure({ id: run.id, open: event.currentTarget.open });
    }} className="min-w-0 rounded-lg border border-border text-sm">
      <summary className="flex min-h-12 cursor-pointer list-none flex-wrap items-center justify-between gap-2 rounded-lg px-4 py-3 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring [&::-webkit-details-marker]:hidden">
        <h2 className="flex items-center gap-2 font-semibold"><ChevronDown className={`h-4 w-4 ${open ? "" : "-rotate-90"}`} aria-hidden />{t("report.agentTrace")}</h2>
        <span role="status" className="flex min-w-0 items-center gap-2 break-words text-xs text-muted-foreground">
          {busy ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin motion-reduce:animate-none" aria-hidden /> : null}{current}
        </span>
      </summary>
      <div className="border-t border-border">
        <div ref={viewport} onScroll={() => {
          const element = viewport.current;
          if (element) follow.current = element.scrollHeight - element.scrollTop - element.clientHeight < 48;
        }} className="max-h-96 overflow-y-auto overscroll-contain px-4 py-2">
          {execution?.traceOmitted ? <p className="py-2 text-xs text-muted-foreground">{t("report.traceOmitted", { count: execution.traceOmitted })}</p> : null}
          {events.length === 0 ? <p className="py-3 text-muted-foreground">{busy ? t("report.traceQueued") : t("report.traceUnavailable")}</p> : null}
          <ol role="log" aria-label={t("report.agentTrace")} aria-live={busy ? "polite" : "off"} aria-relevant="additions text" className="divide-y divide-border">
            {events.map((event) => {
              const step = event.kind === "tool" ? execution?.activity[event.toolIndex ?? -1] : undefined;
              const Icon = event.kind === "tool" ? Wrench : event.kind === "message" ? MessageSquare : event.kind === "completed" ? CheckCircle2 : event.kind === "failed" || event.kind === "timeout" ? XCircle : Circle;
              return (
                <li key={event.id} className="flex min-w-0 items-start gap-3 py-2">
                  <time dateTime={event.at} className="w-20 shrink-0 whitespace-nowrap pt-2 text-xs tabular-nums text-muted-foreground">{formatTime(event.at)}</time>
                  <Icon className="mt-2 h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.5} aria-hidden />
                  {step ? <ToolStep step={step} onInspect={() => { follow.current = false; }} /> : <p className="min-w-0 whitespace-pre-wrap break-words py-1.5">{event.kind === "message" ? event.text : t(`report.traceEvent.${event.kind}`)}</p>}
                </li>
              );
            })}
          </ol>
        </div>
        {execution ? <p className="border-t border-border px-4 py-3 text-xs tabular-nums text-muted-foreground">{t("report.agentReceipt", { harness: execution.harness, model: execution.model ?? t("program.operatorModel"), input: execution.inputTokens === null ? "—" : formatNumber(execution.inputTokens), output: execution.outputTokens === null ? "—" : formatNumber(execution.outputTokens) })}</p> : null}
      </div>
    </details>
  );
}
